import { API_BASE_URL, getAuthHeaders } from '../client';

type MonthlyExportParams = {
  report_type: 'collection-data' | 'visit-data' | 'ptp-visit' | 'cash-collection' | 'repayment_schedule';
  demand_month: number;
  demand_year: number;
  format?: string;
};

type TransactionParams = {
  report_type: 'transaction-data';
  demand_month: number;
  demand_year: number;
  format?: string;
};

type CallingActivityParams = {
  report_type: 'calling-activity';
  start_date: string; // YYYY-MM-DD
  end_date: string;   // YYYY-MM-DD
  format?: string;
};

type DateRangeExportParams = {
  report_type: 'attendance';
  start_date: string; // YYYY-MM-DD
  end_date: string;   // YYYY-MM-DD
  format?: string;
};

type DateBasedExportParams = CallingActivityParams | DateRangeExportParams;

export type ExportDataParams = MonthlyExportParams | TransactionParams | DateBasedExportParams;

export class ExportService {
  /**
   * Export data to Excel format
   * @param params - Export parameters including report_type, demand_month and demand_year
   * @returns Promise that resolves to the Excel file blob
   */
  static async exportData(params: ExportDataParams): Promise<Blob> {
    const commonHeaders = {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };

    let response: Response;
    if (params.report_type === 'transaction-data') {
      const { report_type, demand_month, demand_year, format = 'excel' } = params;
      response = await fetch(`${API_BASE_URL}/export/transaction-data`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ report_type, demand_month, demand_year, format })
      });
    } else if (params.report_type === 'calling-activity') {
      const { report_type, start_date, end_date, format = 'excel' } = params;
      response = await fetch(`${API_BASE_URL}/export/calling-activity`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ report_type, start_date, end_date, format })
      });
    } else if (params.report_type === 'attendance') {
      const { report_type, start_date, end_date, format = 'excel' } = params;
      response = await fetch(`${API_BASE_URL}/export/export`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ report_type, start_date, end_date, format })
      });
    } else if (
      params.report_type === 'collection-data' ||
      params.report_type === 'visit-data' ||
      params.report_type === 'ptp-visit' ||
      params.report_type === 'cash-collection' ||
      params.report_type === 'repayment_schedule'
    ) {
      const { demand_month, demand_year, format = 'excel' } = params;
      // Collection data is sent to the backend as "collection_report"
      const report_type = params.report_type === 'collection-data' ? 'collection_report' : params.report_type;
      response = await fetch(`${API_BASE_URL}/export/export`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ report_type, demand_month, demand_year, format })
      });
    } else {
      // Keeps exhaustive handling for future report_type additions
      throw new Error(`Unsupported report type: ${(params as { report_type: string }).report_type}`);
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${response.status} ${errorText}`);
    }

    return await response.blob();
  }

  /**
   * Download the exported file
   * @param blob - The file blob
   * @param filename - The filename for the download
   */
  static downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Export data and trigger download
   * @param params - Export parameters
   * @param filename - Optional custom filename
   */
  static async exportAndDownload(params: ExportDataParams, filename?: string): Promise<void> {
    try {
      const blob = await this.exportData(params);
      const defaultFilename =
        params.report_type === 'transaction-data'
          ? `transaction_data_${params.demand_month}_${params.demand_year}.xlsx`
          : params.report_type === 'calling-activity'
            ? `calling_activity_${params.start_date}_${params.end_date}.xlsx`
            : params.report_type === 'attendance'
              ? `attendance_${params.start_date}_${params.end_date}.xlsx`
              : params.report_type === 'ptp-visit'
                ? `ptp_visit_report_${params.demand_month}_${params.demand_year}.xlsx`
                : params.report_type === 'collection-data' ||
                    params.report_type === 'visit-data' ||
                    params.report_type === 'cash-collection' ||
                    params.report_type === 'repayment_schedule'
                  ? `${params.report_type.replace('-', '_')}_${params.demand_month}_${params.demand_year}.xlsx`
                : 'export.xlsx';
      this.downloadFile(blob, filename || defaultFilename);
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  }
}
